// src/context/SubscriptionContext.tsx
/* eslint-disable react-refresh/only-export-components */

import React, { createContext, useState, useEffect, useContext, useCallback, useMemo, ReactNode } from 'react';
import { subscriptionService } from '../lib/supabase.ts';
import { useAuth } from '../hooks/useAuth.ts';
import type { SubscriptionTier, SubscriptionStatus, SubscriptionContextType } from '../types/index.ts';

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { user, profile, isAuthenticated, loading: authLoading } = useAuth();
  const [tier, setTier] = useState<SubscriptionTier>('free');
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [periodEnd, setPeriodEnd] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Check if user is admin (admins always have Ember access)
  const isAdmin = profile?.role === 'power_admin' || profile?.role === 'content_admin';

  // Ember is allowed for premium users or admins
  const isEmberAllowed = isAdmin || tier === 'premium';

  const refreshSubscription = useCallback(async () => {
    if (!user?.id) {
      setTier('free');
      setStatus(null);
      setPeriodEnd(null);
      setCancelAtPeriodEnd(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Fetch tier and subscription details in parallel
      const [userTier, subscription] = await Promise.all([
        subscriptionService.getUserTier(user.id),
        subscriptionService.getSubscription(user.id),
      ]);
      setTier(userTier);
      if (subscription) {
        setStatus(subscription.status as SubscriptionStatus);
        setPeriodEnd(subscription.current_period_end);
        setCancelAtPeriodEnd(subscription.cancel_at_period_end);
      } else {
        setStatus(null);
        setPeriodEnd(null);
        setCancelAtPeriodEnd(false);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
      // The profile-sync effect below handles tier fallback from profile changes.
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Load subscription when user changes
  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated || !user?.id) {
      setTier('free');
      setStatus(null);
      setPeriodEnd(null);
      setCancelAtPeriodEnd(false);
      setLoading(false);
      return;
    }

    refreshSubscription();
    // refreshSubscription is intentionally omitted — it's stabilized on user.id and including it
    // re-fires the effect each time the callback identity changes (double-fetch regression).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id, authLoading]);

  // Update tier when profile changes (for admin status)
  useEffect(() => {
    if (profile?.subscription_tier && !loading) {
      setTier(profile.subscription_tier as SubscriptionTier);
    }
  }, [profile?.subscription_tier, loading]);

  const openUpgradeModal = useCallback(() => {
    setShowUpgradeModal(true);
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setShowUpgradeModal(false);
  }, []);

  const contextValue = useMemo<SubscriptionContextType>(() => ({
    tier,
    status,
    isEmberAllowed,
    loading: loading || authLoading,
    periodEnd,
    cancelAtPeriodEnd,
    openUpgradeModal,
    closeUpgradeModal,
    showUpgradeModal,
    refreshSubscription,
  }), [tier, status, isEmberAllowed, loading, authLoading, periodEnd, cancelAtPeriodEnd, openUpgradeModal, closeUpgradeModal, showUpgradeModal, refreshSubscription]);

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export { SubscriptionContext };
